import type { CompanyType, QuickscanAnswers, QuickscanResult } from '@wtta/scoring'

export type WidgetStep =
  | { kind: 'intro' }
  | { kind: 'company-type' }
  | { kind: 'questions'; companyType: CompanyType; questionIndex: number }
  | { kind: 'score-preview'; companyType: CompanyType; result: QuickscanResult }
  | { kind: 'lead-capture'; companyType: CompanyType; result: QuickscanResult; pardotUrl: string; token: string }

export interface WidgetState {
  step: WidgetStep
  companyType: CompanyType | null
  answers: QuickscanAnswers
}

export const initialState: WidgetState = {
  step: { kind: 'intro' },
  companyType: null,
  answers: {},
}

export type WidgetAction =
  | { type: 'start' }
  | { type: 'select-company-type'; companyType: CompanyType }
  | { type: 'answer-question'; questionId: string; value: string }
  | { type: 'go-to-question'; index: number }
  | { type: 'show-score'; result: QuickscanResult }
  | { type: 'show-lead-capture'; result: QuickscanResult; pardotUrl: string; token: string }
  | { type: 'reset' }

export function reducer(state: WidgetState, action: WidgetAction): WidgetState {
  switch (action.type) {
    case 'start':
      return { ...state, step: { kind: 'company-type' } }

    case 'select-company-type':
      return {
        ...state,
        companyType: action.companyType,
        step: { kind: 'questions', companyType: action.companyType, questionIndex: 0 },
      }

    case 'answer-question': {
      if (state.step.kind !== 'questions') return state
      const updatedAnswers = { ...state.answers, [action.questionId]: action.value }
      return { ...state, answers: updatedAnswers }
    }

    case 'go-to-question':
      if (state.step.kind !== 'questions') return state
      return {
        ...state,
        step: { ...state.step, questionIndex: action.index },
      }

    case 'show-score':
      if (!state.companyType) return state
      return {
        ...state,
        step: {
          kind: 'score-preview',
          companyType: state.companyType,
          result: action.result,
        },
      }

    case 'show-lead-capture':
      if (!state.companyType) return state
      return {
        ...state,
        step: {
          kind: 'lead-capture',
          companyType: state.companyType,
          result: action.result,
          pardotUrl: action.pardotUrl,
          token: action.token,
        },
      }

    case 'reset':
      return initialState
  }
}
